/**
 * The staff survey, as sixty teachers will meet it.
 *
 * This is the only surface in the product with no sign-in, no shell and no
 * navigation, and all three are deliberate. Somebody has followed a link from
 * a colleague, on a phone, between classes. Anything on the page that is not
 * the question in front of them is a reason to close the tab.
 *
 * Three decisions worth stating.
 *
 * **One section per screen.** The whole form at once is twenty-two questions
 * and a scrollbar, which reads as a chore before a word of it is answered.
 * A section is four questions and a heading, and the progress is visible
 * without a progress bar telling somebody how much is left.
 *
 * **The reveal mechanism is here and currently shows nothing.** Two panels used
 * to interrupt — after the pay section and after the time section — stopping
 * the page to show what the agreements actually say. Both are withheld at the
 * team's request, because a form that tells you halfway through that you are
 * 10.9% behind is measuring its own effect on you by the end of it. They sat
 * after the section they related to and so could not lead the answers directly
 * beneath them, but four sections follow the pay one.
 *
 * The code stays rather than being torn out: it renders whatever a section
 * carries, and no section currently carries anything. See WITHHELD_REVEALS in
 * the shared question set for the content and for how to put one back.
 *
 * **Nothing is sent until the end.** No partial saves, no telemetry, no
 * "resume later" — a half-answered response that somebody abandoned after the
 * leaving questions is exactly the row that should not exist.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { type Question, type Section } from '@tmea/shared';
import { api } from '../lib/api.js';

type Answers = Record<string, unknown>;

export function Survey({ slug }: { slug: string }) {
  const survey = useQuery({ queryKey: ['survey', slug], queryFn: () => api.survey(slug), retry: false });
  const [answers, setAnswers] = useState<Answers>({});
  const [step, setStep] = useState(0);
  const [showingReveal, setShowingReveal] = useState(false);

  /*
   * Every section, from the first screen.
   *
   * This used to show one section until the reader said what they did, then
   * recompute — the branch clause 4.3 required while aides and instrumental
   * tutors were in scope. They are not: this goes to secondary teaching staff.
   * So the branch cost a whole screen at the point a form is most likely to be
   * abandoned, to ask a question with one true answer.
   */
  const sections: Section[] = useMemo(() => survey.data?.sections ?? [], [survey.data]);

  // Everybody answering is a teacher, so the role is stated rather than asked.
  const submit = useMutation({
    mutationFn: () => api.submitSurvey(slug, 'teacher', answers),
  });

  if (survey.isPending) return <Frame>{null}</Frame>;

  if (!survey.data) {
    return (
      <Frame>
        <h1 className="register-reading text-[22px]">That link doesn&rsquo;t work</h1>
        <p className="measure mt-3 text-[15px] leading-relaxed">
          Check it with whoever sent it to you.
        </p>
      </Frame>
    );
  }

  if (!survey.data.open) {
    return (
      <Frame>
        <h1 className="register-reading text-[22px]">{survey.data.title}</h1>
        <p className="measure mt-3 text-[15px] leading-relaxed">
          This survey has closed. Thank you to everyone who answered.
        </p>
      </Frame>
    );
  }

  if (submit.isSuccess) {
    return (
      <Frame>
        <h1 className="register-reading text-[22px]">Thank you</h1>
        <p className="measure mt-3 text-[15px] leading-relaxed">
          Your answers are in. They go to the bargaining representatives and nobody else, and they
          carry no name against them.
        </p>
      </Frame>
    );
  }

  const section = sections[step];
  if (!section) return <Frame>{null}</Frame>;

  const answered = (q: Question): boolean =>
    q.optional === true || (answers[q.id] !== undefined && answers[q.id] !== '');
  const ready = section.questions.every(answered);
  const last = step === sections.length - 1;

  const next = (): void => {
    if (section.reveal && !showingReveal) {
      setShowingReveal(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setShowingReveal(false);
    if (last) {
      submit.mutate();
      return;
    }
    setStep((s) => s + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Frame>
      {step === 0 ? (
        <header className="mb-7">
          <h1 className="register-reading m-0 text-[24px] leading-[1.25]">{survey.data.title}</h1>
          {survey.data.preamble ? (
            <p className="measure mt-3 text-[14px] leading-relaxed text-[var(--color-muted)]">
              {survey.data.preamble}
            </p>
          ) : null}
        </header>
      ) : null}

      {showingReveal && section.reveal ? (
        <Reveal
          title={section.reveal.title}
          body={section.reveal.body}
          onContinue={next}
          last={last}
        />
      ) : (
        <>
          {/*
            No count until the count is known.

            Before somebody says what they do, only the first section exists —
            clause 4.3 decides whether the pay and load sections are even shown
            — so the header read "1 of 1" on the opening screen, which promises
            a one-question survey and then produces seven more.
          */}
          {sections.length > 1 ? (
            <p className="label mb-1">
              {step + 1} of {sections.length}
            </p>
          ) : null}
          <h2 className="register-reading m-0 text-[19px] leading-snug">{section.title}</h2>
          {section.blurb ? (
            <p className="measure mt-2 text-[13.5px] leading-relaxed text-[var(--color-muted)]">
              {section.blurb}
            </p>
          ) : null}

          <div className="mt-6 flex flex-col gap-7">
            {section.questions.map((q) => (
              <Field
                key={q.id}
                question={q}
                value={answers[q.id]}
                onChange={(v) => setAnswers((prev) => ({ ...prev, [q.id]: v }))}
              />
            ))}
          </div>

          <div className="mt-8 flex items-center gap-4 border-t border-[var(--color-rule)] pt-5">
            <button
              type="button"
              onClick={next}
              disabled={!ready || submit.isPending}
              className="hit rounded-[8px] px-5 py-2.5 text-[15px] disabled:opacity-40"
              style={{ background: 'var(--color-ink)', color: 'var(--color-paper)' }}
            >
              {submit.isPending ? 'Sending…' : last ? 'Submit' : 'Next'}
            </button>
            {step > 0 ? (
              <button
                type="button"
                onClick={() => {
                  setStep((s) => s - 1);
                  setShowingReveal(false);
                }}
                className="hit label underline underline-offset-2"
              >
                Back
              </button>
            ) : null}
            {!ready ? (
              <span className="label text-[10.5px] text-[var(--color-muted)]">
                Answer the questions above to continue
              </span>
            ) : null}
          </div>

          {submit.isError ? (
            <p role="alert" className="mt-4 text-[13px] text-[var(--color-defect)]">
              {(submit.error as Error).message}
            </p>
          ) : null}
        </>
      )}
    </Frame>
  );
}

/**
 * The page itself, with no shell around it.
 *
 * A single column at reading measure. This is read on a phone in a staffroom
 * more often than on a desk, and the mode nav, the rail and the dock are
 * exactly what this reader does not need.
 */
function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen px-5 py-10">
      <div className="mx-auto max-w-[640px]">{children}</div>
    </div>
  );
}

/**
 * What the agreements actually say, shown after the section it belongs to.
 *
 * Set apart from the questions rather than styled as one, because it is not a
 * question — it is the moment the survey stops asking and tells. It is also
 * the only screen here a respondent might want to read twice, so it does not
 * advance until they say so.
 */
function Reveal({
  title,
  body,
  onContinue,
  last,
}: {
  title: string;
  body: string;
  onContinue: () => void;
  last: boolean;
}) {
  return (
    <div>
      <p className="label mb-1">While you are here</p>
      <h2 className="register-reading m-0 text-[19px] leading-snug">{title}</h2>
      <div className="mt-4 rounded-[10px] border border-[var(--color-rule-strong)] px-4 py-4">
        {body.split(/\n{2,}/).map((para, i) => (
          <p
            key={i}
            className={`measure mb-3 text-[13.5px] leading-relaxed last:mb-0 ${
              /^\s{2}/.test(para) ? 'clause-number whitespace-pre text-[12px]' : ''
            }`}
          >
            {para}
          </p>
        ))}
      </div>
      <div className="mt-7 border-t border-[var(--color-rule)] pt-5">
        <button
          type="button"
          onClick={onContinue}
          className="hit rounded-[8px] px-5 py-2.5 text-[15px]"
          style={{ background: 'var(--color-ink)', color: 'var(--color-paper)' }}
        >
          {last ? 'Submit' : 'Continue'}
        </button>
      </div>
    </div>
  );
}

/** One question, in whichever of the five shapes it declares. */
function Field({
  question,
  value,
  onChange,
}: {
  question: Question;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  const id = `q-${question.id}`;
  const help = question.help ? `${id}-help` : undefined;

  return (
    <div>
      <label htmlFor={id} className="block text-[15px] leading-snug">
        {question.prompt}
        {question.optional ? (
          <span className="label ml-2 text-[10.5px] text-[var(--color-muted)]">optional</span>
        ) : null}
      </label>

      {/*
        Context sits under the question rather than behind an icon.

        An info icon is a thing to notice, decide about and tap, three times per
        section, on a phone. The context here is two sentences and it is the
        reason the question is being asked — putting it one interaction away
        means most people answer without it.
      */}
      {question.help ? (
        <p id={help} className="measure mt-1.5 text-[12.5px] leading-relaxed text-[var(--color-muted)]">
          {question.help}
        </p>
      ) : null}

      <div className="mt-2.5">
        {question.kind === 'text' ? (
          <textarea
            id={id}
            aria-describedby={help}
            rows={question.lines ?? 3}
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onChange(e.target.value)}
            className="w-full rounded-[8px] border border-[var(--color-rule-strong)] px-3 py-2.5 text-[15px] leading-relaxed outline-none"
          />
        ) : null}

        {question.kind === 'number' ? (
          <span className="flex items-baseline gap-2">
            <input
              id={id}
              aria-describedby={help}
              type="number"
              inputMode="decimal"
              min={0}
              max={question.max}
              value={typeof value === 'number' || typeof value === 'string' ? String(value) : ''}
              onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
              className="clause-number w-28 rounded-[8px] border border-[var(--color-rule-strong)] px-3 py-2.5 text-[15px] outline-none"
            />
            {question.unit ? <span className="label">{question.unit}</span> : null}
          </span>
        ) : null}

        {question.kind === 'choice' ? (
          <div className="flex flex-col gap-2">
            {question.choices.map((c) => (
              <label
                key={c.value}
                className="hit flex cursor-pointer items-start gap-2.5 rounded-[8px] border px-3 py-2.5 text-[14px] leading-snug"
                style={{
                  borderColor: value === c.value ? 'var(--color-ink)' : 'var(--color-rule)',
                }}
              >
                <input
                  type="radio"
                  name={id}
                  checked={value === c.value}
                  onChange={() => onChange(c.value)}
                  className="mt-0.5"
                />
                {c.label}
              </label>
            ))}
          </div>
        ) : null}

        {question.kind === 'multi' ? (
          <MultiSelect question={question} value={value} onChange={onChange} />
        ) : null}

        {question.kind === 'rank' ? <Ranker question={question} value={value} onChange={onChange} /> : null}
      </div>
    </div>
  );
}

function MultiSelect({
  question,
  value,
  onChange,
}: {
  question: Extract<Question, { kind: 'multi' }>;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  const chosen = Array.isArray(value) ? (value as string[]) : [];
  const full = question.max !== undefined && chosen.length >= question.max;

  return (
    <div className="flex flex-wrap gap-2">
      {question.choices.map((c) => {
        const on = chosen.includes(c.value);
        return (
          <button
            key={c.value}
            type="button"
            aria-pressed={on}
            /*
              Disabled once the limit is reached rather than silently dropping
              the extra. "Choose up to three" that quietly ignores a fourth tap
              reads as a broken button.
            */
            disabled={!on && full}
            onClick={() =>
              onChange(on ? chosen.filter((v) => v !== c.value) : [...chosen, c.value])
            }
            className="hit rounded-[8px] border px-3 py-2 text-[13.5px] disabled:opacity-35"
            style={{
              borderColor: on ? 'var(--color-ink)' : 'var(--color-rule)',
              background: on ? 'var(--color-subtle)' : undefined,
            }}
          >
            {c.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Ordering, without drag and drop.
 *
 * Dragging ten items is unpleasant on a desk and close to unusable on a phone,
 * which is where most of these will be answered. Tapping in order gives the
 * same ranking, works with a keyboard and a screen reader for free, and shows
 * the position as it is chosen — so somebody who only cares about their top
 * three can stop after three.
 */
function Ranker({
  question,
  value,
  onChange,
}: {
  question: Extract<Question, { kind: 'rank' }>;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  const order = Array.isArray(value) ? (value as string[]) : [];

  return (
    <div>
      <p className="label mb-2 text-[10.5px] text-[var(--color-muted)]">
        Tap in order, most important first. Tap again to remove.
      </p>
      <div className="flex flex-col gap-1.5">
        {question.choices.map((c) => {
          const at = order.indexOf(c.value);
          return (
            <button
              key={c.value}
              type="button"
              onClick={() =>
                onChange(at === -1 ? [...order, c.value] : order.filter((v) => v !== c.value))
              }
              className="hit flex items-center gap-3 rounded-[8px] border px-3 py-2.5 text-left text-[14px]"
              style={{ borderColor: at === -1 ? 'var(--color-rule)' : 'var(--color-ink)' }}
            >
              <span
                className="clause-number w-6 shrink-0 text-[12px]"
                style={{ color: at === -1 ? 'var(--color-muted)' : 'var(--color-ink)' }}
              >
                {at === -1 ? '·' : at + 1}
              </span>
              {c.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
